/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Checkbox,
    ListItemText
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";

function DateRangePicker() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState("");
    const [syncId, setSyncId] = useState("");
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [summary, setSummary] = useState(null);
    const [status, setStatus] = useState("");

    // New state for statuses
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const statusOptions = ["Draft", "Authorised", "Paid"];

    const handleStatusChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
        if (error) setError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!startDate || !endDate) {
            setError("Both start and end dates are required.");
            return;
        }
        if (startDate > endDate) {
            setError("Start date cannot be after end date.");
            return;
        }

        if (selectedStatuses.length === 0) {
            setError("Select at least one status.");
            return;
        }

        const tenantId = sessionStorage.getItem("tenantId");

        if (!tenantId) {
            setError("Missing tenant information. Please log in again.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            // Append selected statuses as a query param
            // Build multiple statuses query params
            const statusParams = selectedStatuses
                .map((s) => `statuses=${encodeURIComponent(s.toUpperCase())}`)
                .join("&");
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/sync-creditnotes?tenantId=${tenantId}&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}&${statusParams}`,
                {
                    method: "GET",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    },
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch Data.");
            }

            const data = await response.json();
            setSyncId(data.syncId);
            setPolling(true);
        } catch (err) {
            setError(err.message || "An error occurred while fetching Data.");
        } finally {
            setLoading(false);
        }
    };

    // Polling logic remains unchanged...
    useEffect(() => {
        let interval;
        if (polling && syncId) {
            interval = setInterval(async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/sync-invoices/${syncId}/progress?tenantId=${sessionStorage.getItem("tenantId")}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                                "Authorization": `${sessionStorage.getItem('token')}`
                            },
                        }
                    );
                    if (response.status === 401) {
                        handleSessionExpiredClose();
                        return;
                    }
                    if (!response.ok) {
                        throw new Error("Failed to fetch sync status.");
                    }
                    const data = await response.json();
                    setStatus(data.status);
                    setSummary(data.summary);

                    if (data.status === "Completed" || data.status === "Failed") {
                        setPolling(false);
                        clearInterval(interval);
                    }
                } catch (err) {
                    setError("An error occurred while polling for sync status.");
                    setPolling(false);
                    clearInterval(interval);
                }
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [polling, syncId]);

    const minDate = new Date(2022, 0, 1);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ alignItems: "center" }} gap={2} p={2}>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
                >
                    Sync Credit Notes
                </Typography>
            </Box>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "66%",
                    maxWidth: 800,
                    margin: "auto",
                    padding: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                    Select Date Range
                </Typography>
                <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    {/* Left column: Start Date + Status Dropdown */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "50%" }}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            shouldDisableDate={(date) => date < minDate}
                            slotProps={{ textField: { fullWidth: true } }}
                            onChange={(newValue) => {
                                setStartDate(newValue);
                                if (error) setError("");
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                multiple
                                value={selectedStatuses}
                                onChange={handleStatusChange}
                                renderValue={(selected) => selected.join(", ")}
                            >
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                                        <ListItemText primary={status} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Right column: End Date */}
                    <Box sx={{ width: "50%" }}>
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            shouldDisableDate={(date) => date < minDate}
                            slotProps={{ textField: { fullWidth: true } }}
                            onChange={(newValue) => {
                                setEndDate(newValue);
                                if (error) setError("");
                            }}
                        />
                    </Box>
                </Box>


                {error && (
                    <Typography color="error" sx={{ textAlign: "center" }}>
                        {error}
                    </Typography>
                )}
                {syncId && (
                    <Typography color="success" sx={{ textAlign: "center" }}>
                        Sync initiated successfully!
                    </Typography>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || polling}
                    sx={{ alignSelf: "center" }}
                >
                    {loading || polling ? (
                        <>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            {polling ? "Polling..." : "Syncing..."}
                        </>
                    ) : (
                        "Sync"
                    )}
                </Button>
            </Box>

            {polling && (
                <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                    Current status: {status}
                </Typography>
            )}

            {/* Summary table remains unchanged */}
            {summary && (
                <Paper
                    sx={{
                        width: "66%",
                        maxWidth: 800,
                        margin: "24px auto 0 auto",
                        padding: 2,
                        boxShadow: 3,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", textAlign: "center" }}
                    >
                        Sync Summary
                    </Typography>
                    <TableContainer
                        sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            borderRadius: 1,
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell
                                        sx={{
                                            fontWeight: "bold",
                                            fontSize: 16,
                                            width: "50%",
                                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: "bold",
                                            fontSize: 16,
                                            width: "50%",
                                        }}
                                    >
                                        Count
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {["requested", "processed", "alreadyExist", "failed", "completed"].map(
                                    (key, index) => (
                                        <TableRow
                                            key={key}
                                            sx={{
                                                backgroundColor:
                                                    index % 2 === 0 ? "white" : "grey.100",
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    borderRight:
                                                        "1px solid rgba(224, 224, 224, 1)",
                                                    fontSize: 16,
                                                }}
                                            >
                                                {key.toUpperCase()}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 16 }}>
                                                {summary[key] || 0}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </LocalizationProvider>
    );
}

export default DateRangePicker;
